import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3011/api'

const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
})

// Add request interceptor to include JWT token
axiosInstance.interceptors.request.use(
    config => {
        const token = localStorage.getItem('token')
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }
        return config
    },
    error => {
        return Promise.reject(error)
    }
)

// Add response interceptor to handle token expiration
axiosInstance.interceptors.response.use(
    response => response,
    error => {
        if (error.response?.status === 401) {
            // Token expired or invalid, clear storage and redirect to login
            localStorage.removeItem('token')
            localStorage.removeItem('user')
            window.location.href = '/login'
        }
        return Promise.reject(error)
    }
)

export const apiClient = {
    login: async (email: string, password: string) => {
        const response = await axiosInstance.post('/auth/login', { email, password })
        return response.data
    },

    changePassword: async (userId: number, newPassword: string) => {
        const response = await axiosInstance.put('/auth/change-password', { userId, newPassword })
        return response.data
    },

    getUsers: async () => {
        const response = await axiosInstance.get('/users')
        return response.data
    },

    getProducts: async () => {
        const response = await axiosInstance.get('/products')
        return response.data
    },

    getApplications: async () => {
        const response = await axiosInstance.get('/applications')
        return response.data
    },

    getApplicationById: async (id: string) => {
        const response = await axiosInstance.get(`/applications/${id}`)
        return response.data
    },

    getApplicationStatusHistory: async (id: string) => {
        const response = await axiosInstance.get(`/applications/${id}/status-history`)
        return response.data
    },

    getApplicationDocuments: async (id: string) => {
        const response = await axiosInstance.get(`/applications/${id}/documents`)
        return response.data
    },

    updateApplicationStatus: async (id: string, statusId: number, decisionDetails?: any) => {
        const payload: any = { statusId }
        if (decisionDetails) {
            payload.decisionDetails = decisionDetails
        }
        const response = await axiosInstance.put(`/applications/${id}/status`, payload)
        return response.data
    },

    getApiTraffic: async () => {
        const response = await axiosInstance.get('/traffic')
        return response.data
    },

    getApiTrafficById: async (id: string) => {
        const response = await axiosInstance.get(`/traffic/${id}`)
        return response.data
    },

    deleteApplication: async (id: string) => {
        const response = await axiosInstance.delete(`/applications/${id}`)
        return response.data
    },

    getBrokers: async () => {
        const response = await axiosInstance.get('/brokers')
        return response.data
    },

    getBrokerById: async (id: string) => {
        const response = await axiosInstance.get(`/brokers/${id}`)
        return response.data
    },

    createBroker: async (broker: any) => {
        const response = await axiosInstance.post('/brokers', broker)
        return response.data
    },

    updateBroker: async (id: string, broker: any) => {
        const response = await axiosInstance.put(`/brokers/${id}`, broker)
        return response.data
    },

    createBrokerWebhook: async (brokerId: string, webhook: any) => {
        const response = await axiosInstance.post(`/brokers/${brokerId}/webhooks`, webhook)
        return response.data
    },

    updateBrokerWebhook: async (webhookId: string, webhook: any) => {
        const response = await axiosInstance.put(`/webhooks/${webhookId}`, webhook)
        return response.data
    },

    deleteBrokerWebhook: async (webhookId: string) => {
        const response = await axiosInstance.delete(`/webhooks/${webhookId}`)
        return response.data
    },

    getWebhookTypes: async () => {
        const response = await axiosInstance.get('/webhook-types')
        return response.data
    },

    getLenders: async () => {
        const response = await axiosInstance.get('/lenders')
        return response.data
    },

    submitToLenders: async (applicationId: string, lenderIds: string[]) => {
        const response = await axiosInstance.post(`/applications/${applicationId}/submit-to-lenders`, { LenderIds: lenderIds })
        return response.data
    },
}
